import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Globe, Shield, MessageSquare, BookOpen } from "lucide-react";

export const Footer = memo(function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="border-t border-border bg-muted/30 mt-auto">
      <div className="container py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Browse */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {t("footer.browse")}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/browse">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.allListings")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/browse?category=vehicles">
                  <a className="hover:text-primary transition-colors">
                    {t("categories.vehicles")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/browse?category=electronics">
                  <a className="hover:text-primary transition-colors">
                    {t("categories.electronics")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/browse?category=jobs">
                  <a className="hover:text-primary transition-colors">
                    {t("categories.jobs")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/browse?category=housing">
                  <a className="hover:text-primary transition-colors">
                    {t("categories.housing")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/apps">
                  <a className="hover:text-primary transition-colors font-medium text-primary">
                    {t("footer.apps")}
                  </a>
                </Link>
              </li>
            </ul>
          </div>

          {/* Sell */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {t("footer.sell")}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/post">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.postAd")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/dashboard">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.manageAds")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/surveys">
                  <a className="hover:text-primary transition-colors">
                    {t("nav.surveys")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/profile">
                  <a className="hover:text-primary transition-colors">
                    {t("profile.title")}
                  </a>
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {t("footer.support")}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/safety-guidelines">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.safetyGuidelines")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/contact">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.contact")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/help">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.help")}
                  </a>
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t("footer.about")}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/about">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.about")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/terms">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.terms")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/privacy">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.privacy")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/careers">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.careers")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/press">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.press")}
                  </a>
                </Link>
              </li>
              <li>
                <Link href="/blog">
                  <a className="hover:text-primary transition-colors">
                    {t("footer.blog")}
                  </a>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link href="/">
              <a className="text-2xl font-bold gradient-text">Listo</a>
            </Link>
            <span className="text-sm text-muted-foreground">
              {t("footer.copyright")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";
